import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Button, Stack, Alert } from "@mui/material";
import { FileButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUpload, IconTrash } from "@tabler/icons-react";
import { CardPost } from "../components/Card/CardPost";
import { PageContainer } from "../components/Container/PageContainer";
import { useUser } from "../contexts/UserContext";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  getIdeations,
  uploadIdeationsFromJSON,
  deleteIdeation,
} from "../utils/ideationUtils";
import { useTranslation } from "react-i18next";

const Ideation = () => {
  const { t } = useTranslation();
  const { profile } = useUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile("md");

  const [ideations, setIdeations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // 아이디어 목록 로드
  const loadIdeations = async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);
      const data = await getIdeations(profile.id);
      setIdeations(data);
    } catch (error) {
      console.error('[Ideation] Failed to load ideations:', error);
      notifications.show({
        title: '오류',
        message: error.message || '아이디어 목록을 불러오는데 실패했습니다.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIdeations();
  }, [profile?.id]);

  // JSON 파일 업로드 처리
  const handleFileUpload = async (file) => {
    if (!file) return;

    try {
      setUploading(true);

      // 파일 읽기
      const text = await file.text();
      let jsonData;

      try {
        jsonData = JSON.parse(text);
      } catch (parseError) {
        throw new Error('유효한 JSON 파일이 아닙니다.');
      }

      // 배열 검증
      if (!Array.isArray(jsonData)) {
        throw new Error('JSON 파일은 배열 형식이어야 합니다.');
      }

      // 유효한 데이터 필터링
      const validItems = jsonData.filter(
        (item) => item && typeof item.text === 'string' && item.text.trim().length > 0
      );

      if (validItems.length === 0) {
        throw new Error('유효한 아이디어가 없습니다. [{text: "내용"}, ...] 형식을 확인해주세요.');
      }

      // Firestore에 업로드
      const uploadedCount = await uploadIdeationsFromJSON(profile.id, validItems);

      notifications.show({
        title: '업로드 완료',
        message: `${uploadedCount}개의 아이디어가 업로드되었습니다.`,
        color: 'green',
      });

      // 목록 새로고침
      await loadIdeations();
    } catch (error) {
      console.error('[Ideation] Upload failed:', error);
      notifications.show({
        title: '업로드 실패',
        message: error.message || 'JSON 파일 업로드에 실패했습니다.',
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  // 아이디어 삭제
  const handleDelete = async (ideationId, e) => {
    e.stopPropagation(); // 카드 클릭 이벤트 방지

    if (!window.confirm('이 아이디어를 삭제하시겠습니까?')) return;

    try {
      await deleteIdeation(profile.id, ideationId);
      notifications.show({
        title: '삭제 완료',
        message: '아이디어가 삭제되었습니다.',
        color: 'green',
      });
      await loadIdeations();
    } catch (error) {
      console.error('[Ideation] Delete failed:', error);
      notifications.show({
        title: '삭제 실패',
        message: error.message || '아이디어 삭제에 실패했습니다.',
        color: 'red',
      });
    }
  };

  // 아이디어 클릭 - 수정 페이지로 이동
  const handleCardClick = (ideationId) => {
    navigate(`/ideation/${ideationId}`);
  };

  return (
    <PageContainer expand={true}>
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 2, md: 3 } }}>
        {/* 헤더 */}
        <Stack spacing={2} sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Ideation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            아이디어를 JSON 파일로 일괄 업로드하고 관리하세요. 각 아이디어를 클릭하여 수정하거나 임시글로 내보낼 수 있습니다.
          </Typography>
        </Stack>

        {/* 업로드 버튼 */}
        <Box sx={{ mb: 4 }}>
          <FileButton
            onChange={handleFileUpload}
            accept="application/json,.json"
            disabled={uploading || !profile?.id}
          >
            {(props) => (
              <Button
                {...props}
                variant="contained"
                startIcon={<IconUpload size={20} />}
                disabled={uploading || !profile?.id}
                sx={{ textTransform: 'none' }}
              >
                {uploading ? '업로드 중...' : 'JSON 파일 업로드'}
              </Button>
            )}
          </FileButton>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            JSON 형식: [{`{text: "아이디어 내용"}, {text: "다음 아이디어"}, ...`}]
          </Typography>
        </Box>

        {/* 로딩 상태 */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>로딩 중...</Typography>
          </Box>
        )}

        {/* 빈 상태 */}
        {!loading && ideations.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            아직 업로드된 아이디어가 없습니다. JSON 파일을 업로드하여 시작하세요.
          </Alert>
        )}

        {/* 아이디어 그리드 */}
        {!loading && ideations.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              아이디어 목록 ({ideations.length}개)
            </Typography>
            <Grid container spacing={2}>
              {ideations.map((ideation) => {
                // CardPost에 맞게 데이터 변환
                const threadForCard = {
                  text: ideation.text,
                  permalink: null, // ideation은 외부 링크 없음
                  views: 0,
                  likes: 0,
                  replies: 0,
                  reposts: 0,
                  viewRank: 999,
                  engagementRank: 999,
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={ideation.id}>
                    <Box
                      onClick={() => handleCardClick(ideation.id)}
                      sx={{ position: 'relative', cursor: 'pointer' }}
                    >
                      {/* 삭제 버튼 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                        }}
                      >
                        <Button
                          size="small"
                          color="error"
                          variant="contained"
                          onClick={(e) => handleDelete(ideation.id, e)}
                          sx={{
                            minWidth: 'auto',
                            width: 32,
                            height: 32,
                            p: 0,
                            borderRadius: '50%',
                          }}
                        >
                          <IconTrash size={16} />
                        </Button>
                      </Box>

                      {/* CardPost 재사용 */}
                      <CardPost
                        thread={threadForCard}
                        date={ideation.createdAt?.toDate ? ideation.createdAt.toDate() : new Date(ideation.createdAt)}
                        isMobile={isMobile}
                        isMetric={false} // 아이디어는 메트릭 없음
                        rd={8}
                        borderType="full"
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};

export default Ideation;
